import { faro } from '@grafana/faro-web-sdk'
import type { CodeRunFinished } from '~/utils/codeRunTracker'
import type { PageNavigation } from '~/utils/pageNavigationTimer'

// Central, type-safe emitter for business/domain Faro events (funnel analytics).
//
// This complements the automatic `page_view` navigation events wired in
// `application/plugins/9.faro.client.ts`: page_view answers "which pages did the
// user visit?", while these domain events answer "did the user actually START /
// SUBMIT / COMPLETE an assessment?" — the conversions a raw route change can't tell.
//
// Every event carries dimensions (not just an occurrence to count) so Loki/Grafana
// can slice by `is_sample` (practice vs real), assessment, task type, completion
// depth, etc. Combined with Faro's automatic per-event context (session_id, user_id,
// timestamp, page_url, browser), this supports funnels, conversion/drop-off rates,
// per-assessment breakdowns and per-user journeys — not only totals.
//
// PII policy (mirrors the plugin): emit OPAQUE IDs + low-cardinality dimensions only.
// NEVER send email, real name, submitted code, or score. The logged-in user is already
// correlated via `faro.api.setUser({ id: uuid })`, so join on uuid for personal detail.
//
// Faro event attributes are `Record<string, string>`, so every value is coerced to a
// string and null/undefined attributes are dropped (booleans -> "true"/"false").
// Tracking is best-effort and must never break a user flow — a no-op when Faro isn't
// initialised (SSR / local / CI / hosts with no collector via resolveFaro), and
// swallowed on any error.

type FaroEventAttrs = Record<string, string | number | boolean | null | undefined>

type ProblemContext = 'proSkillAssure' | 'level' | 'contest'

export type EnvPrecheckType = 'basic_screen_share' | 'external_proctive'

// `assessment`: business funnel events. `platform`: platform health / performance events
// (code run outcome & latency, SPA navigation latency) — kept apart so funnel queries
// and platform SLO dashboards never mix.
type EventDomain = 'assessment' | 'platform'

export const useFaroTracking = () => {
  const track = (name: string, attrs: FaroEventAttrs = {}, domain: EventDomain = 'assessment') => {
    // Guard: faro is only initialised in the browser on hosts that resolve a
    // collector. Everywhere else `faro.api` is absent — do nothing.
    if (!faro?.api?.pushEvent) return

    const stringAttrs: Record<string, string> = {}
    for (const [key, value] of Object.entries(attrs)) {
      if (value === null || value === undefined) continue
      stringAttrs[key] = String(value)
    }

    try {
      faro.api.pushEvent(name, stringAttrs, domain)
    } catch {
      // Never let observability break the assessment flow.
    }
  }

  return {
    /** Candidate accepted the assessment rules (funnel: access / READY step). */
    trackAssessmentRulesAgreed: (attrs: { assessmentId: number; isSample: boolean; sampleType?: string }) =>
      track('assessment_rules_agreed', {
        assessment_id: attrs.assessmentId,
        is_sample: attrs.isSample,
        sample_type: attrs.sampleType,
      }),

    /** Candidate pressed [응시 환경 점검 시작]. Loki keeps only this `event_name` for a year,
     *  so it must stay identical to the one in `alloy-config.alloy.tpl`.
     *  샘플 평가는 점검 게이트를 지나지 않아 여기로 오지 않는다 — 기록 대상으로 확정되면
     *  그때 `is_sample` 을 속성으로 더한다. */
    trackEnvPrecheckStarted: (attrs: { assessmentId: number; checkType: EnvPrecheckType }) =>
      track('assessment_env_precheck_started', {
        assessment_id: attrs.assessmentId,
        check_type: attrs.checkType,
      }),

    /** Candidate started the assessment (funnel: IN_PROGRESS). */
    trackAssessmentStarted: (attrs: { assessmentId: number; isSample: boolean; questionCount?: number; sampleType?: string }) =>
      track('assessment_started', {
        assessment_id: attrs.assessmentId,
        is_sample: attrs.isSample,
        question_count: attrs.questionCount,
        sample_type: attrs.sampleType,
      }),

    /** Candidate finished / submitted the whole assessment (funnel: COMPLETED). */
    trackAssessmentCompleted: (attrs: { assessmentId: number; isSample: boolean; completedCount?: number; totalCount?: number }) =>
      track('assessment_completed', {
        assessment_id: attrs.assessmentId,
        is_sample: attrs.isSample,
        completed_count: attrs.completedCount,
        total_count: attrs.totalCount,
      }),

    /**
     * Screen recording force-aborted: consecutive empty (header-only) blobs detected.
     * Carries the recent blob-size history and capture-track state so production
     * reports can be diagnosed from logs alone — this path shows the same fail
     * dialog as a getDisplayMedia rejection but previously left no telemetry.
     */
    trackRecordingAborted: (attrs: {
      domainType: string
      domainId?: number
      blobSizeHistory: string
      consecutiveEmptyCount: number
      trackMuted?: boolean
      trackReadyState?: string
    }) =>
      track('recording_aborted', {
        domain_type: attrs.domainType,
        domain_id: attrs.domainId,
        blob_size_history: attrs.blobSizeHistory,
        consecutive_empty_count: attrs.consecutiveEmptyCount,
        track_muted: attrs.trackMuted,
        track_ready_state: attrs.trackReadyState,
      }),

    /** Screen-share capture track ended (user pressed "stop sharing", display change, OS revoked). */
    trackRecordingShareEnded: (attrs: {
      domainType: string
      domainId?: number
      isExtendedDisplay: boolean
      handledByMultiMonitorFlow: boolean
      trackMuted?: boolean
      trackReadyState?: string
    }) =>
      track('recording_share_ended', {
        domain_type: attrs.domainType,
        domain_id: attrs.domainId,
        is_extended_display: attrs.isExtendedDisplay,
        handled_by_multi_monitor_flow: attrs.handledByMultiMonitorFlow,
        track_muted: attrs.trackMuted,
        track_ready_state: attrs.trackReadyState,
      }),

    /** Candidate submitted an answer to a single problem (real submit, not run). */
    trackProblemSubmitted: (attrs: {
      context: ProblemContext
      assessmentId?: number
      taskId?: number
      templateId?: number
      isSample?: boolean
      taskType?: string
      isLastQuestion?: boolean
    }) =>
      track('problem_submitted', {
        context: attrs.context,
        assessment_id: attrs.assessmentId,
        task_id: attrs.taskId,
        template_id: attrs.templateId,
        is_sample: attrs.isSample,
        task_type: attrs.taskType,
        is_last_question: attrs.isLastQuestion,
      }),

    /** One per Run click: how the coderun ended and how long it took (platform C3/C4). */
    trackCodeRunFinished: (run: CodeRunFinished) =>
      track('code_run_finished', {
        run_id: run.runId,
        lang_type: run.langType,
        task_type: run.taskType,
        domain: run.domain,
        domain_id: run.domainId,
        task_id: run.taskId,
        outcome: run.outcome,
        first_output_ms: run.firstOutputMs,
        total_ms: run.totalMs,
        ws_retry_count: run.wsRetryCount,
      }, 'platform'),

    /** One per in-app route change: navigation start → page rendered (platform C7). */
    trackPageNavigation: (navigation: PageNavigation) =>
      track('page_navigation', {
        route: navigation.route,
        from_route: navigation.fromRoute,
        duration_ms: navigation.durationMs,
      }, 'platform'),
  }
}
